import { formatDate } from '../i18n/format';
import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { useRef, useState, type MouseEvent } from 'react';
import { Table, Select, Tooltip, Popconfirm, type SelectProps } from 'antd';
import { MessageOutlined, EditOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import TaskHeartbeatControl from './TaskHeartbeatControl';
import { ExecutionUsageChip } from './ExecutionUsageChip';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';
import ParticipantAvatars from './ParticipantAvatars';
import { useTaskStore } from '../stores/useTaskStore';
import { STATUS_CONFIG } from '../lib/taskStatus';
import { compareTasksWithIdTieBreak } from '../lib/taskOrdering';
import * as api from '../lib/api';
import { useToast } from './Toast';
import type { ChannelMember } from '../lib/api';

interface Props {
  tasks: api.Task[];
  members: ChannelMember[];
  channelMap?: Record<string, string>;
  channelId?: string;
  activeThreadId?: string;
  onTaskClick?: (task: api.Task) => void;
  /** Title goes to the task's canonical conversation; Thread action stays in-place. */
  onTaskTitleClick?: (task: api.Task) => void;
  onChannelClick?: (task: api.Task) => void;
  onEditTask?: (task: api.Task) => void;
}

export default function TasksTableView({ tasks, members, channelMap, channelId, activeThreadId, onTaskClick, onTaskTitleClick, onChannelClick, onEditTask }: Props) {
  const { i18n } = useLingui();
  const { showToast } = useToast();
  const deletingTaskIdsRef = useRef(new Set<string>());
  const [deletingTaskIds, setDeletingTaskIds] = useState<Set<string>>(() => new Set());

  const handleDelete = async (task: api.Task) => {
    if (deletingTaskIdsRef.current.has(task.id)) return;

    deletingTaskIdsRef.current.add(task.id);
    setDeletingTaskIds(current => new Set(current).add(task.id));
    try {
      await api.deleteTask(task.id);
      useTaskStore.getState().removeTask(task.id);
      showToast(msg`Task deleted`);
    } catch (err) {
      showToast(errorMessage(err, msg`Failed to delete task`));
    } finally {
      deletingTaskIdsRef.current.delete(task.id);
      setDeletingTaskIds(current => {
        const next = new Set(current);
        next.delete(task.id);
        return next;
      });
    }
  };

  return (
    <div className="tasks-table-scroll">
    <Table
      dataSource={tasks}
      rowKey="id"
      size="small"
      pagination={false}
      // The table has several semantically non-compressible columns. Its
      // full-height outer pane owns the deliberate horizontal scroll, so the
      // scrollbar remains at the bottom of the content surface even with only
      // a few rows instead of floating immediately below the data.
      style={{ minWidth: 1280 }}
      tableLayout="fixed"
      sticky
      onRow={(task: api.Task) => ({ onClick: () => onTaskClick?.(task) })}
      rowClassName={(task: api.Task) => task.thread_id === activeThreadId ? 'task-row-active' : ''}
      columns={[
        {
          title: i18n._(msg`ID`), dataIndex: 'seq', key: 'seq', width: 70,
          sorter: (a: api.Task, b: api.Task) => compareTasksWithIdTieBreak((left, right) => left.seq - right.seq, a, b),
          render: (seq: number) => <span style={{ color: '#888' }}>#{seq}</span>,
        },
        {
          title: i18n._(msg`Title`), dataIndex: 'title', key: 'title', width: 350,
          sorter: (a: api.Task, b: api.Task) => compareTasksWithIdTieBreak((left, right) => left.title.localeCompare(right.title), a, b),
          render: (_: string, task: api.Task) => {
            const deleting = deletingTaskIds.has(task.id);
            const stopPropagation = (event?: MouseEvent<HTMLElement>) => event?.stopPropagation();
            return <span className="task-title-cell">
              <span className="task-title-cell-text">
                {!channelId && channelMap?.[task.channel_id] && (
                  <span className="task-card-channel">#{channelMap[task.channel_id]}/</span>
                )}
                <button type="button" className="task-title-link" onClick={(event) => {
                  event.stopPropagation();
                  onTaskTitleClick?.(task);
                }}>{task.title}</button>
              </span>
              <span className="task-title-actions">
                <TaskHeartbeatControl task={task} size={14} />
                {task.thread_id && <button type="button" className="task-card-btn" onClick={e => { e.stopPropagation(); onTaskClick?.(task); }} aria-label={i18n._(msg`Open thread`)}><MessageOutlined aria-hidden="true" /></button>}
                {onEditTask && <button type="button" className="task-card-btn" onClick={e => { e.stopPropagation(); onEditTask(task); }} aria-label={i18n._(msg`Edit task`)}><EditOutlined aria-hidden="true" /></button>}
                <Popconfirm
                  title={i18n._(msg`Delete task?`)}
                  description={i18n._(msg`Are you sure you want to delete task #${task.seq}?`)}
                  okText={i18n._(msg`Delete`)}
                  cancelText={i18n._(msg`Cancel`)}
                  okButtonProps={{ danger: true, loading: deleting, disabled: deleting }}
                  onConfirm={(event?: MouseEvent<HTMLElement>) => { stopPropagation(event); return handleDelete(task); }}
                  onCancel={stopPropagation}
                >
                  <Tooltip title={i18n._(msg`Delete task`)}><button type="button" className="task-card-btn" aria-label={i18n._(msg`Delete task`)} disabled={deleting} onClick={stopPropagation}><DeleteOutlined aria-hidden="true" /></button></Tooltip>
                </Popconfirm>
              </span>
            </span>;
          },
        },
        {
          title: i18n._(msg`Status`), dataIndex: 'status', key: 'status', width: 140,
          sorter: (a: api.Task, b: api.Task) => compareTasksWithIdTieBreak((left, right) => left.status.localeCompare(right.status), a, b),
          render: (status: string, task: api.Task) => {
            const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
            return (
              <Select
                size="small"
                variant="borderless"
                value={status}
                style={{ width: '100%' }}
                onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
                onChange={async (newStatus: string) => {
                  useTaskStore.getState().upsertTask({ ...task, status: newStatus as api.Task['status'], updated_at: Date.now() });
                  await api.updateTask(task.id, { status: newStatus });
                }}
                options={Object.entries(STATUS_CONFIG).map(([key, c]) => ({
                  value: key,
                  label: <span style={{ color: c.color }}>{c.icon} {i18n._(c.label)}</span>,
                }))}
                labelRender={(props: Parameters<NonNullable<SelectProps['labelRender']>>[0]) => {
                  const value = typeof props.value === 'string' ? props.value : String(props.value ?? '');
                  const c = STATUS_CONFIG[value as keyof typeof STATUS_CONFIG];
                  return c ? <span style={{ color: c.color }}>{value === 'in_progress' ? <SyncOutlined aria-hidden="true" spin /> : c.icon} {i18n._(c.label)}</span> : <span>{value}</span>;
                }}
              />
            );
          },
        },
        ...(!channelId ? [{
          title: i18n._(msg`Channel`), key: 'channel', width: 190,
          render: (_: unknown, task: api.Task) => {
            const source = task.channel;
            if (!source) return channelMap?.[task.channel_id] ? <span>#{channelMap[task.channel_id]}</span> : <span style={{ color: '#888' }}>—</span>;
            return <button type="button" className="task-channel-link" onClick={(event) => {
              event.stopPropagation();
              onChannelClick?.(task);
            }} aria-label={i18n._(msg`Open ${source.guild_name} #${source.channel_name}`)}>
              <span>{source.guild_name}</span><span>#{source.channel_name}</span>
            </button>;
          },
        }] : []),
        {
          title: i18n._(msg`Usage`), key: 'usage', width: 110,
          render: (_: unknown, task: api.Task) => task.usage_summary && task.usage_summary.coverage !== 'unavailable'
            ? <ExecutionUsageChip resourceId={task.id} subscriptionId={task.thread_id} scope="task" deferred initialSummary={task.usage_summary} />
            : null,
        },
        {
          title: i18n._(msg`Assignee`), dataIndex: 'assignee_id', key: 'assignee', width: 150,
          sorter: (a: api.Task, b: api.Task) => compareTasksWithIdTieBreak((left, right) => (left.assignee_id ?? '').localeCompare(right.assignee_id ?? ''), a, b),
          render: (assigneeId: string, task: api.Task) => {
            const assignee = task.assignee;
            if (!assignee) return null;
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserAvatar userId={assignee.id} displayName={assignee.display_name} size={18} />
                <UserName displayName={assignee.display_name} role={assignee.role} />
              </span>
            );
          },
        },
        {
          title: i18n._(msg`Participants`), key: 'participants', width: 160,
          render: (_: unknown, task: api.Task) => {
            if (!task.participants || task.participants.length === 0) return null;
            const participants = task.participant_profiles?.map(person => ({ user_id: person.id, display_name: person.display_name, avatar_url: person.avatar_url }))
              ?? task.participants.map(uid => {
                const m = members.find(mem => mem.user_id === uid);
                return { user_id: uid, display_name: m?.display_name ?? uid, avatar_url: m?.avatar_url ?? '' };
              });
            return <ParticipantAvatars participants={participants} size={18} max={4} />;
          },
        },
        {
          title: i18n._(msg`Updated`), dataIndex: 'updated_at', key: 'updated_at', width: 110,
          sorter: (a: api.Task, b: api.Task) => compareTasksWithIdTieBreak((left, right) => (left.updated_at ?? left.created_at) - (right.updated_at ?? right.created_at), a, b),
          defaultSortOrder: 'descend' as const,
          render: (ts: number) => {
            const d = new Date(ts);
            return <span style={{ color: '#888', fontSize: 12 }}>{formatDate(i18n, d, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>;
          },
        },
      ]}
    />
    </div>
  );
}
